import { useMemo, useState } from "react";

import { CommunicationPreferenceFormType, hasPermissionOnContacts, Locales } from "@simplyk/common";
import { enqueueSnackbar } from "notistack";

import { CommunicationPreferenceOption, CommunicationPreferenceOptionNameResult } from "../types";

import {
  CommunicationPreferencesDraft,
  usePublishCommunicationPreferencesDraft,
} from "./communicationPreferencesDraft";
import { getFormCommunicationPreferencesMutationScope } from "./formCommunicationPreferencesMutationScope";
import { useDefaultQuestionTitle } from "./useDefaultQuestionTitle";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ContactListType } from "@/features/ContactFiltering/types";
import { getDeleteContactListErrorKey } from "@/features/ContactList/helpers/getDeleteContactListErrorKey";
import { useCustomQuestionsTracking } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/helpers/useCustomQuestionsTracking";

const NO_OPTIONS: CommunicationPreferenceOption[] = [];

interface UseCommunicationPreferencesFormOptions {
  formId?: string;
  formType?: CommunicationPreferenceFormType;
  formLocale: Locales;
  enabled?: boolean;
  entryPoint?: "live_form_editor" | "signup_form";
  onSaved?: () => void;
}

export const useCommunicationPreferencesForm = ({
  formId,
  formType,
  formLocale,
  enabled = true,
  entryPoint = "live_form_editor",
  onSaved,
}: UseCommunicationPreferencesFormOptions) => {
  const { t } = useTranslate();
  const { currentUser } = useCurrentUserContext();
  // Renaming or deleting a list goes through the contact lists API, which requires the Contacts permission.
  const canManageOptions = hasPermissionOnContacts(currentUser?.newPermissions);
  const utils = trpc.useUtils();
  const logCustomQuestionsEvent = useCustomQuestionsTracking();
  const { logAmplitudeEvent } = useAmplitude();

  const {
    data: formPreferences,
    isLoading: isLoadingFormPreferences,
    isFetching: isFetchingFormPreferences,
  } = trpc.getFormCommunicationPreferences.useQuery(
    { formId: formId!, formType: formType! },
    { enabled: Boolean(formId && formType && enabled) }
  );

  const { mutateAsync: ensureDefaultOptionMutation, isPending: isEnsuringDefaultOption } =
    trpc.ensureDefaultCommunicationPreferenceOption.useMutation();
  const { mutateAsync: createOption } = trpc.createCommunicationPreferenceOption.useMutation();
  const { mutateAsync: renameContactList } = trpc.renameContactList.useMutation();
  const { mutateAsync: deleteContactList, isPending: isDeletingOption } = trpc.deleteContactList.useMutation();
  const { mutateAsync: updateFormPreferences, isPending: isSaving } =
    trpc.updateFormCommunicationPreferences.useMutation({
      scope: getFormCommunicationPreferencesMutationScope(formId, formType),
    });

  // Only edits live in state; the server selection is derived so a refetch cannot overwrite them.
  const [editedSelection, setEditedSelection] = useState<string[] | null>(null);
  const [editedQuestionTitle, setEditedQuestionTitle] = useState<string | null>(null);
  const defaultQuestionTitle = useDefaultQuestionTitle(formLocale, enabled);

  const options: CommunicationPreferenceOption[] = formPreferences?.options ?? NO_OPTIONS;

  const hasNoOption = Boolean(formPreferences) && options.length === 0;

  // Only the server can create the default option, since it picks a free list name.
  const ensureDefaultOption = async () => {
    const { error } = await ensureDefaultOptionMutation({
      defaultName: t("dashboard", "communicationPreferences.defaultOptionName"),
    });
    if (error) {
      enqueueSnackbar(t("dashboard", "communicationPreferences.saveError"), { vibe: "danger" });
      return;
    }
    await Promise.all([
      utils.getFormCommunicationPreferences.invalidate(),
      utils.getCommunicationPreferenceOptions.invalidate(),
      utils.getContactLists.invalidate(),
    ]);
  };

  const seededSelection = useMemo(() => {
    if (!formPreferences) {
      return [];
    }
    if (formPreferences.selectedContactListIds.length > 0) {
      return formPreferences.selectedContactListIds;
    }
    return [...options]
      .sort((first, second) => Number(second.isDefault) - Number(first.isDefault))
      .map((option) => option.contactListId);
  }, [formPreferences, options]);

  const selectedContactListIds = editedSelection ?? seededSelection;
  const savedQuestionTitle = formPreferences?.questionTitle ?? null;
  // Null until the default title loads, so the field waits rather than flashing empty.
  const questionTitle = editedQuestionTitle ?? savedQuestionTitle ?? defaultQuestionTitle;
  // Undefined until edited, so a save that leaves the title alone keeps the saved one.
  const editedCustomQuestionTitle =
    editedQuestionTitle === null ? undefined : toCustomQuestionTitle(editedQuestionTitle, defaultQuestionTitle);

  const draft = useMemo<CommunicationPreferencesDraft | null>(
    () =>
      enabled && formId && formType && (editedSelection || editedCustomQuestionTitle !== undefined)
        ? {
            formId,
            formType,
            selectedContactListIds,
            questionTitle: editedCustomQuestionTitle === undefined ? savedQuestionTitle : editedCustomQuestionTitle,
          }
        : null,
    [enabled, formId, formType, editedSelection, selectedContactListIds, editedCustomQuestionTitle, savedQuestionTitle]
  );
  usePublishCommunicationPreferencesDraft(draft);

  // A save sent while a list is being deleted would link it back to the form or fail on it.
  const canSave = selectedContactListIds.length > 0 && !isDeletingOption;

  const invalidateOptions = () =>
    Promise.all([
      utils.getFormCommunicationPreferences.invalidate(),
      utils.getCommunicationPreferenceOptions.invalidate(),
      utils.getContactLists.invalidate(),
      utils.getContactCommunicationPreferences.invalidate(),
    ]);

  const reportNameError = (error: { code: string } | null | undefined): CommunicationPreferenceOptionNameResult => {
    if (error?.code === "contact_list_name_already_exists") {
      return "duplicate";
    }
    enqueueSnackbar(t("dashboard", "communicationPreferences.saveError"), { vibe: "danger" });
    return "failed";
  };

  const handleCreateOption = async (name: string): Promise<CommunicationPreferenceOptionNameResult> => {
    const { data, error } = await createOption({ name });
    if (error || !data) {
      return reportNameError(error);
    }

    setEditedSelection([...selectedContactListIds, data.contactListId]);
    await invalidateOptions();
    return "saved";
  };

  const handleRenameOption = async (
    contactListId: string,
    name: string
  ): Promise<CommunicationPreferenceOptionNameResult> => {
    const { error } = await renameContactList({ listId: contactListId, newName: name });
    if (error) {
      return reportNameError(error);
    }

    await invalidateOptions();
    return "saved";
  };

  // Deleting archives the list, which also takes it off every form, so it cannot wait for the save.
  const handleDeleteOption = async (contactListId: string): Promise<boolean> => {
    // A save still in flight would rewrite the form's links after the delete and undo it.
    if (isSaving) {
      return false;
    }
    const { error } = await deleteContactList({ listId: contactListId });
    if (error) {
      enqueueSnackbar(t("dashboard", getDeleteContactListErrorKey(error.code)), { vibe: "danger" });
      return false;
    }

    logAmplitudeEvent(AmplitudeEvents.ContactListDeleted, {
      ListType: ContactListType.Filter,
      From: "Communication Preferences Modal",
    });
    setEditedSelection((current) => {
      const remaining = (current ?? selectedContactListIds).filter((selectedId) => selectedId !== contactListId);
      // A sign-up form left without options was moved to the default one, so the refetched selection is the one to show.
      return remaining.length === 0 && formType === CommunicationPreferenceFormType.NewsletterForm ? null : remaining;
    });
    await invalidateOptions();
    enqueueSnackbar(t("dashboard", "crm.lists.deleteSuccess"), { vibe: "positive" });
    return true;
  };

  const handleSave = async () => {
    if (!formId || !formType || !canSave) {
      return;
    }

    const contactListIds = selectedContactListIds.filter((contactListId) =>
      options.some((option) => option.contactListId === contactListId)
    );

    const { data: savedPreferences, error } = await updateFormPreferences({
      formId,
      formType,
      contactListIds,
      questionTitle: editedCustomQuestionTitle,
    });
    if (error || !savedPreferences) {
      enqueueSnackbar(t("dashboard", "communicationPreferences.saveError"), { vibe: "danger" });
      return;
    }

    logCustomQuestionsEvent(AmplitudeEvents.CommunicationPreferencesConfigured, {
      count_options: contactListIds.length,
      entry_point: entryPoint,
    });

    utils.getFormCommunicationPreferences.setData({ formId, formType }, savedPreferences);
    void utils.getCommunicationPreferenceOptions.invalidate();
    void utils.getContactLists.invalidate();
    onSaved?.();
  };

  const resetState = () => {
    setEditedSelection(null);
    setEditedQuestionTitle(null);
  };

  return {
    options,
    selectedContactListIds,
    setSelectedContactListIds: setEditedSelection,
    questionTitle,
    defaultQuestionTitle,
    setQuestionTitle: setEditedQuestionTitle,
    canManageOptions,
    // A just-created default option reads as empty until the refetch lands.
    isLoading: isLoadingFormPreferences || isEnsuringDefaultOption || (hasNoOption && isFetchingFormPreferences),
    isSaving,
    canSave,
    handleCreateOption,
    handleRenameOption,
    handleDeleteOption,
    handleSave,
    ensureDefaultOption,
    resetState,
  };
};

// An empty or default title is saved as null, so contacts read the default in their own language.
const toCustomQuestionTitle = (questionTitle: string, defaultQuestionTitle: string | null) => {
  const trimmedQuestionTitle = questionTitle.trim();
  return trimmedQuestionTitle && trimmedQuestionTitle !== defaultQuestionTitle ? trimmedQuestionTitle : null;
};
